import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Trash2, History, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface Prompt {
  id: string;
  raw_prompt: string;
  refined_prompt: string;
  category: string;
  created_at: string;
  score?: number;
}

interface AllHistoryModalProps {
  prompts: Prompt[];
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
  onCopy: (text: string) => void;
  onViewDetails: (prompt: Prompt) => void;
  categoryOptions: { value: string; label: string }[];
}

const AllHistoryModal = ({
  prompts,
  isOpen,
  onClose,
  onDelete,
  onCopy,
  onViewDetails,
  categoryOptions,
}: AllHistoryModalProps) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);

  const filteredPrompts = prompts.filter((prompt) => {
    const matchesSearch = prompt.refined_prompt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || prompt.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <History className="w-6 h-6" />
            Prompt History
          </DialogTitle>
          <DialogDescription>
            View and manage all your previous prompts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Search and Filter */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              {categoryOptions.map((category) => (
                <Badge
                  key={category.value}
                  variant={selectedCategory === category.value ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(selectedCategory === category.value ? null : category.value)}
                >
                  {category.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Prompts List */}
          <div className="space-y-4">
            {filteredPrompts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No prompts found matching your criteria
              </div>
            ) : (
              filteredPrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="p-4 rounded-lg border border-border hover:border-primary transition-colors cursor-pointer"
                  onClick={() => onViewDetails(prompt)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">
                      {categoryOptions.find(cat => cat.value === prompt.category)?.label}
                    </Badge>
                    <div className="flex items-center gap-2">
                      {prompt.score && (
                        <Badge variant="secondary" className={cn(
                          "bg-primary/10",
                          prompt.score >= 80 ? "text-green-500" :
                          prompt.score >= 50 ? "text-yellow-500" :
                          "text-red-500"
                        )}>
                          Score: {prompt.score}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopy(prompt.refined_prompt);
                        }}
                        className="h-8 w-8 p-0 hover:text-primary"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(prompt.id);
                        }}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive/90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-foreground line-clamp-2 mb-2">
                    {prompt.refined_prompt}
                  </p>
                  <div className="text-xs text-muted-foreground">
                    {new Date(prompt.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AllHistoryModal; 